import { useEffect, useRef, useState } from 'react';

import { throttle } from 'lodash';
import { useRafLoop } from 'react-use';
import type { Apollo } from 'src/gql/generated';

import { getNextIndex } from '../helpers/typewriter-helpers';

const updateSpeedMultiplier = throttle((gap: number, speedRef: React.MutableRefObject<number>) => {
  if (gap > 500) speedRef.current = 8;
  if (gap > 400) speedRef.current = 6;
  else if (gap > 300) speedRef.current = 5;
  else if (gap > 200) speedRef.current = 4;
  else if (gap > 100) speedRef.current = 2;
  else speedRef.current = 1;
}, 5000);

/**
 * Bulletproof typewriter hook using useRafLoop.
 */
export function useTypewriter(args: {
  isMessageStreaming: boolean;
  message: Pick<Apollo.AgentMessage, 'id' | 'text'>;
}): string {
  const speedRef = useRef(1);
  const CHARS_PER_SECOND = 100;
  const lastTimeRef = useRef(0);
  const { isMessageStreaming, message } = args;
  const targetTextRef = useRef(message.text);
  const previousMessageIdRef = useRef(message.id);
  const displayIndexRef = useRef(message.text.length);
  const [displayedText, setDisplayedText] = useState(message.text);

  useEffect(() => {
    if (message.id !== previousMessageIdRef.current) {
      lastTimeRef.current = 0;
      setDisplayedText(message.text);
      targetTextRef.current = message.text;
      previousMessageIdRef.current = message.id;
      displayIndexRef.current = message.text.length;
    }
  }, [message.id, message.text]);

  const [loopStop, loopStart, isActive] = useRafLoop(time => {
    // Initialize lastTimeRef on first frame to avoid huge delta...
    if (lastTimeRef.current === 0) {
      lastTimeRef.current = time;
      return;
    }

    const deltaTimeSeconds = (time - lastTimeRef.current) / 1000;
    lastTimeRef.current = time;

    const gapBetweenDisplayTextAndStreamedText =
      targetTextRef.current.length - displayIndexRef.current;
    // This causes typing speed to speed up/slow down depending on the gap.
    updateSpeedMultiplier(gapBetweenDisplayTextAndStreamedText, speedRef);

    // Round up so fractions below 1 character are shown...
    const charsToShow = Math.ceil(deltaTimeSeconds * CHARS_PER_SECOND * speedRef.current);
    const tentativeNextIndex = Math.min(
      displayIndexRef.current + charsToShow,
      targetTextRef.current.length
    );

    // Get the actual next index, accounting for tool call blocks...
    const newIndex = getNextIndex(
      targetTextRef.current,
      displayIndexRef.current,
      tentativeNextIndex
    );

    displayIndexRef.current = newIndex;
    setDisplayedText(targetTextRef.current.slice(0, newIndex));

    if (newIndex >= targetTextRef.current.length) {
      lastTimeRef.current = 0;
      loopStop();
    }
  }, false);

  useEffect(() => {
    if (!isMessageStreaming && !isActive()) {
      setDisplayedText(message.text);
      return;
    }

    if (message.text === targetTextRef.current) return;

    // New content arrived...
    targetTextRef.current = message.text;

    // Start animation if not already running...
    if (!isActive()) loopStart();
  }, [message.text, isMessageStreaming, isActive, loopStart, loopStop]);

  return displayedText;
}
